import { Flex, Form, Table, TableProps, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { COMPANY_ROLE_ENUM, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { ReactNode, useEffect, useState } from "react";

import { updateCompanyFees, updateRequestFeeSettings } from "services/settingsService";
import { useStores } from "stores";
import { ActionModalConfig } from "stores/ActionModalsStore";
import { components } from "types/openapi";
import { CustomButton, NumberInput } from "ui";
import { formatWithDecimals, requestWithNotify, toFractionOrZero, toNumberOrZero, useFormSubmitDisabled } from "utils";
import {
  _greaterOrEqual,
  _isRequired,
  _lessOrEqual,
  _lessOrEqualToComputed,
  _lessThan,
  _maxDecimals,
} from "utils/form/simple-validation";
import styles from "./UpdateAdditionalFeesModal.module.css";

type BaseFeesFieldType = components["schemas"]["SetSettingsRequest"];
type RequestFeeSettingsPayload = components["schemas"]["UpdateRequestFeeSettingsRequest"];

type UpdateAdditionalFeesModalProps = {
  config?: ActionModalConfig;
};

type FeeFormFields = BaseFeesFieldType & {
  customerAcquiringFeeImp: number | string;
  customerAgentFeeImp: number | string;
  customerAgentFeeExp: number | string;
  customerAdditionalRequestsFeeImp: number | string;
  customerAdditionalRequestsFeeExp: number | string;
  customerAmountDiscountImp: number | string;
  customerAmountDiscountExp: number | string;
  customerDiscountThresholdImp: number | string;
  customerDiscountThresholdExp: number | string;
  lpAcquiringFeeExp: number | string;
  lpAmountDiscountImp: number | string;
  lpAmountDiscountExp: number | string;
  lpDiscountThresholdImp: number | string;
  lpDiscountThresholdExp: number | string;
};

type FeeFieldName = keyof FeeFormFields;

type TableCell = {
  node: ReactNode;
};

type FeeTableRow = {
  key: string;
  name: string;
  baseFee: TableCell;
  acquiringFee: TableCell;
  agentFee: TableCell;
  additionalRequestsFee: TableCell;
  amountDiscount: TableCell;
  discountThreshold: TableCell;
};

const toPercent = (value: number | null | undefined) =>
  value !== null && value !== undefined ? formatWithDecimals(value * 100) : "";

const EDIT_FEE_MODAL_TITLE = "Редактирование комиссии сделки";
const CONFIRM_FEE_MODAL_TITLE = "Подтвердите действие";
const EDIT_FEE_MODAL_WIDTH = "1200px";
const CONFIRM_FEE_MODAL_WIDTH = "600px";
const REQUIRED_MESSAGE = "Поле обязательно для ввода";
const DISCOUNT_EXCEEDS_FEE_MESSAGE = "Скидка за сумму не может превышать ставку комиссий";
const REQUIRED_FEE_FIELDS: FeeFieldName[] = [
  "customerBaseFeeExp",
  "customerBaseFeeImp",
  "lpBaseFeeExp",
  "lpBaseFeeImp",
  "customerAcquiringFeeImp",
  "customerAgentFeeImp",
  "customerAgentFeeExp",
  "customerAdditionalRequestsFeeImp",
  "customerAdditionalRequestsFeeExp",
  "customerAmountDiscountImp",
  "customerAmountDiscountExp",
  "customerDiscountThresholdImp",
  "customerDiscountThresholdExp",
  "lpAcquiringFeeExp",
  "lpAmountDiscountImp",
  "lpAmountDiscountExp",
  "lpDiscountThresholdImp",
  "lpDiscountThresholdExp",
];

const notCharged = <Typography.Text className="text-description">Не взимается</Typography.Text>;
const cell = (node: ReactNode): TableCell => ({ node });

export const UpdateAdditionalFeesModal = observer(({ config }: UpdateAdditionalFeesModalProps) => {
  const { actionModals, settings } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [isConfirmStep, setIsConfirmStep] = useState(false);

  const [form] = Form.useForm<FeeFormFields>();
  const submitDisabled = useFormSubmitDisabled({
    form,
    loading,
    requiredFields: REQUIRED_FEE_FIELDS,
  });

  useEffect(() => {
    if (!config) {
      return;
    }

    config.options.title = isConfirmStep ? CONFIRM_FEE_MODAL_TITLE : EDIT_FEE_MODAL_TITLE;
    config.options.width = isConfirmStep ? CONFIRM_FEE_MODAL_WIDTH : EDIT_FEE_MODAL_WIDTH;
  }, [config, isConfirmStep]);

  const findFeeSettings = (role: string, direction: "IMP" | "EXP") => {
    const settingsByDirection = settings.requestFeeSettings.find((item) => item.role === role && item.direction === direction);

    return settingsByDirection || settings.requestFeeSettings.find((item) => item.role === role);
  };

  const customerImpFees = findFeeSettings(COMPANY_ROLE_ENUM.CUSTOMER, "IMP");
  const customerExpFees = findFeeSettings(COMPANY_ROLE_ENUM.CUSTOMER, "EXP");
  const lpImpFees = findFeeSettings(COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER, "IMP");
  const lpExpFees = findFeeSettings(COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER, "EXP");

  const computeDiscountLimit = (fields: FeeFieldName[]) => (getFieldValue: (name: string | string[]) => any) =>
    fields.reduce((sum, field) => sum + toNumberOrZero(getFieldValue(field)), 0);

  const baseFeeMaxRule = _lessThan(100, "Комиссия должна быть меньше 100%");
  const baseFeeRules = [_isRequired(REQUIRED_MESSAGE), _greaterOrEqual(0), _maxDecimals(2), baseFeeMaxRule];
  const percentFeeRules = [_isRequired(REQUIRED_MESSAGE), _greaterOrEqual(0), _maxDecimals(2), _lessOrEqual(100)];

  const renderFeeInput = (
    name: FeeFieldName,
    rules: ReturnType<typeof _isRequired>[],
    unit: string,
    dependencies?: FeeFieldName[],
    width = 130
  ) => (
    <Form.Item
      name={name}
      dependencies={dependencies}
      rules={rules}
      validateDebounce={FORM_VALIDATION_DEBOUNCE}
      style={{ marginBottom: 0, width }}
      className={styles.compactFeeInput}
    >
      <NumberInput disabled={loading} size="small" suffix={unit} />
    </Form.Item>
  );

  const columns: TableProps<FeeTableRow>["columns"] = [
    {
      title: "Участник",
      dataIndex: "name",
      key: "name",
      width: 190,
    },
    {
      title: "Основная ставка",
      dataIndex: "baseFee",
      key: "baseFee",
      width: 130,
      render: (value: TableCell) => value.node,
    },
    {
      title: "Комиссия за эквайринг",
      dataIndex: "acquiringFee",
      key: "acquiringFee",
      width: 160,
      render: (value: TableCell) => value.node,
    },
    {
      title: "За платёжного агента",
      dataIndex: "agentFee",
      key: "agentFee",
      width: 150,
      render: (value: TableCell) => value.node,
    },
    {
      title: "За доп. заявки",
      dataIndex: "additionalRequestsFee",
      key: "additionalRequestsFee",
      width: 140,
      render: (value: TableCell) => value.node,
    },
    {
      title: "Скидка за сумму",
      dataIndex: "amountDiscount",
      key: "amountDiscount",
      width: 140,
      render: (value: TableCell) => value.node,
    },
    {
      title: "Порог для скидки",
      dataIndex: "discountThreshold",
      key: "discountThreshold",
      width: 150,
      render: (value: TableCell) => value.node,
    },
  ];

  const dataSource: FeeTableRow[] = [
    {
      key: "customer-import",
      name: "Участник ВЭД (Импорт)",
      baseFee: cell(renderFeeInput("customerBaseFeeImp", baseFeeRules, "%")),
      acquiringFee: cell(renderFeeInput("customerAcquiringFeeImp", percentFeeRules, "%")),
      agentFee: cell(renderFeeInput("customerAgentFeeImp", percentFeeRules, "%")),
      additionalRequestsFee: cell(renderFeeInput("customerAdditionalRequestsFeeImp", percentFeeRules, "%")),
      amountDiscount: cell(
        renderFeeInput(
          "customerAmountDiscountImp",
          [
            ...percentFeeRules,
            _lessOrEqualToComputed(
              computeDiscountLimit([
                "customerBaseFeeImp",
                "customerAcquiringFeeImp",
                "customerAgentFeeImp",
                "customerAdditionalRequestsFeeImp",
              ]),
              DISCOUNT_EXCEEDS_FEE_MESSAGE
            ),
          ],
          "%",
          ["customerBaseFeeImp", "customerAcquiringFeeImp", "customerAgentFeeImp", "customerAdditionalRequestsFeeImp"]
        )
      ),
      discountThreshold: cell(
        renderFeeInput("customerDiscountThresholdImp", [_isRequired(REQUIRED_MESSAGE), _greaterOrEqual(0)], "руб.")
      ),
    },
    {
      key: "customer-export",
      name: "Участник ВЭД (Экспорт)",
      baseFee: cell(renderFeeInput("customerBaseFeeExp", baseFeeRules, "%")),
      acquiringFee: cell(notCharged),
      agentFee: cell(renderFeeInput("customerAgentFeeExp", percentFeeRules, "%")),
      additionalRequestsFee: cell(renderFeeInput("customerAdditionalRequestsFeeExp", percentFeeRules, "%")),
      amountDiscount: cell(
        renderFeeInput(
          "customerAmountDiscountExp",
          [
            ...percentFeeRules,
            _lessOrEqualToComputed(
              computeDiscountLimit(["customerBaseFeeExp", "customerAgentFeeExp", "customerAdditionalRequestsFeeExp"]),
              DISCOUNT_EXCEEDS_FEE_MESSAGE
            ),
          ],
          "%",
          ["customerBaseFeeExp", "customerAgentFeeExp", "customerAdditionalRequestsFeeExp"]
        )
      ),
      discountThreshold: cell(
        renderFeeInput("customerDiscountThresholdExp", [_isRequired(REQUIRED_MESSAGE), _greaterOrEqual(0)], "руб.")
      ),
    },
    {
      key: "lp-import",
      name: "ПЛ (Импорт)",
      baseFee: cell(renderFeeInput("lpBaseFeeImp", baseFeeRules, "%")),
      acquiringFee: cell(notCharged),
      agentFee: cell(notCharged),
      additionalRequestsFee: cell(notCharged),
      amountDiscount: cell(
        renderFeeInput(
          "lpAmountDiscountImp",
          [
            ...percentFeeRules,
            _lessOrEqualToComputed(computeDiscountLimit(["lpBaseFeeImp"]), DISCOUNT_EXCEEDS_FEE_MESSAGE),
          ],
          "%",
          ["lpBaseFeeImp"]
        )
      ),
      discountThreshold: cell(
        renderFeeInput("lpDiscountThresholdImp", [_isRequired(REQUIRED_MESSAGE), _greaterOrEqual(0)], "руб.")
      ),
    },
    {
      key: "lp-export",
      name: "ПЛ (Экспорт)",
      baseFee: cell(renderFeeInput("lpBaseFeeExp", baseFeeRules, "%")),
      acquiringFee: cell(renderFeeInput("lpAcquiringFeeExp", percentFeeRules, "%")),
      agentFee: cell(notCharged),
      additionalRequestsFee: cell(notCharged),
      amountDiscount: cell(
        renderFeeInput(
          "lpAmountDiscountExp",
          [
            ...percentFeeRules,
            _lessOrEqualToComputed(
              computeDiscountLimit(["lpBaseFeeExp", "lpAcquiringFeeExp"]),
              DISCOUNT_EXCEEDS_FEE_MESSAGE
            ),
          ],
          "%",
          ["lpBaseFeeExp", "lpAcquiringFeeExp"]
        )
      ),
      discountThreshold: cell(
        renderFeeInput("lpDiscountThresholdExp", [_isRequired(REQUIRED_MESSAGE), _greaterOrEqual(0)], "руб.")
      ),
    },
  ];

  const buildBaseFeesPayload = (values: FeeFormFields) => ({
    customerBaseFeeExp: toFractionOrZero(values.customerBaseFeeExp),
    customerBaseFeeImp: toFractionOrZero(values.customerBaseFeeImp),
    lpBaseFeeExp: toFractionOrZero(values.lpBaseFeeExp),
    lpBaseFeeImp: toFractionOrZero(values.lpBaseFeeImp),
  });

  const buildRequestFeeSettingsPayload = (values: FeeFormFields): RequestFeeSettingsPayload => ({
    feeSettings: [
      {
        role: COMPANY_ROLE_ENUM.CUSTOMER,
        direction: "IMP",
        acquiringFee: toFractionOrZero(values.customerAcquiringFeeImp),
        agentFee: toFractionOrZero(values.customerAgentFeeImp),
        additionalRequestsFee: toFractionOrZero(values.customerAdditionalRequestsFeeImp),
        amountDiscount: toFractionOrZero(values.customerAmountDiscountImp),
        discountThreshold: toNumberOrZero(values.customerDiscountThresholdImp),
      },
      {
        role: COMPANY_ROLE_ENUM.CUSTOMER,
        direction: "EXP",
        agentFee: toFractionOrZero(values.customerAgentFeeExp),
        additionalRequestsFee: toFractionOrZero(values.customerAdditionalRequestsFeeExp),
        amountDiscount: toFractionOrZero(values.customerAmountDiscountExp),
        discountThreshold: toNumberOrZero(values.customerDiscountThresholdExp),
      },
      {
        role: COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER,
        direction: "IMP",
        amountDiscount: toFractionOrZero(values.lpAmountDiscountImp),
        discountThreshold: toNumberOrZero(values.lpDiscountThresholdImp),
      },
      {
        role: COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER,
        direction: "EXP",
        acquiringFee: toFractionOrZero(values.lpAcquiringFeeExp),
        amountDiscount: toFractionOrZero(values.lpAmountDiscountExp),
        discountThreshold: toNumberOrZero(values.lpDiscountThresholdExp),
      },
    ],
  });

  const handleCancelEditFee = () => {
    actionModals.closeLastModal();
  };

  const saveAllFees = async () => {
    const values = form.getFieldsValue(true) as FeeFormFields;
    const baseFeesPayload = buildBaseFeesPayload(values);
    const requestFeeSettingsPayload = buildRequestFeeSettingsPayload(values);

    const res = await requestWithNotify({
      fetchFn: () => updateCompanyFees(baseFeesPayload),
      openNotification,
      showErrorNotification: true,
    });

    if (!res.result) {
      return false;
    }

    const requestFeeSettingsRes = await requestWithNotify({
      fetchFn: () => updateRequestFeeSettings(requestFeeSettingsPayload),
      openNotification,
      showErrorNotification: true,
      delay: 500,
    });

    return requestFeeSettingsRes.result;
  };

  const approvedFields = async () => {
    try {
      await form.validateFields();
      setIsConfirmStep(true);
    } catch {
      // Ant Design highlights invalid fields after validation fails.
    }
  };

  const confirmAllFees = async () => {
    setLoading(true);

    try {
      const isSaved = await saveAllFees();

      if (isSaved) {
        openNotification({
          message: "Настройки комиссий обновлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          settings.fetchCompanyFees();
          settings.fetchRequestFeeSettings();
        }, 1500);
        handleCancelEditFee();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex vertical>
      <Form
        form={form}
        name="editAdditionalFees"
        initialValues={{
          customerBaseFeeExp: toPercent(settings.companyFees.customerBaseFeeExp),
          customerBaseFeeImp: toPercent(settings.companyFees.customerBaseFeeImp),
          lpBaseFeeExp: toPercent(settings.companyFees.lpBaseFeeExp),
          lpBaseFeeImp: toPercent(settings.companyFees.lpBaseFeeImp),
          customerAcquiringFeeImp: toPercent(customerImpFees?.acquiringFee),
          customerAgentFeeImp: toPercent(customerImpFees?.agentFee),
          customerAgentFeeExp: toPercent(customerExpFees?.agentFee),
          customerAdditionalRequestsFeeImp: toPercent(customerImpFees?.additionalRequestsFee),
          customerAdditionalRequestsFeeExp: toPercent(customerExpFees?.additionalRequestsFee),
          customerAmountDiscountImp: toPercent(customerImpFees?.amountDiscount),
          customerAmountDiscountExp: toPercent(customerExpFees?.amountDiscount),
          customerDiscountThresholdImp:
            customerImpFees?.discountThreshold !== undefined
              ? formatWithDecimals(customerImpFees.discountThreshold, 2)
              : "",
          customerDiscountThresholdExp:
            customerExpFees?.discountThreshold !== undefined
              ? formatWithDecimals(customerExpFees.discountThreshold, 2)
              : "",
          lpAcquiringFeeExp: toPercent(lpExpFees?.acquiringFee),
          lpAmountDiscountImp: toPercent(lpImpFees?.amountDiscount),
          lpAmountDiscountExp: toPercent(lpExpFees?.amountDiscount),
          lpDiscountThresholdImp:
            lpImpFees?.discountThreshold !== undefined ? formatWithDecimals(lpImpFees.discountThreshold, 2) : "",
          lpDiscountThresholdExp:
            lpExpFees?.discountThreshold !== undefined ? formatWithDecimals(lpExpFees.discountThreshold, 2) : "",
        }}
      >
        {isConfirmStep ? (
          <Flex vertical gap="middle">
            <Typography.Text>Вы уверены, что хотите изменить настройки комиссий и скидок?</Typography.Text>
            <Flex gap="large" justify="end">
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="primary"
                onClick={confirmAllFees}
                className="full-width"
              >
                Подтвердить
              </CustomButton>
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={() => setIsConfirmStep(false)}
                className="full-width"
              >
                Отмена
              </CustomButton>
            </Flex>
          </Flex>
        ) : (
          <Flex vertical gap="middle">
            <Table
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              size="small"
              bordered
              className={styles.feesTable}
            />
            <Form.Item>
              <Flex gap="large" justify="end">
                <CustomButton disabled={submitDisabled} loading={loading} type="primary" onClick={approvedFields}>
                  Сохранить
                </CustomButton>
                <CustomButton
                  htmlType="button"
                  disabled={loading}
                  loading={loading}
                  type="default"
                  onClick={handleCancelEditFee}
                >
                  Отменить
                </CustomButton>
              </Flex>
            </Form.Item>
          </Flex>
        )}
      </Form>
    </Flex>
  );
});
